import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js'
import { DEFAULT_PHONE_COUNTRY } from '@/constants/registration'

/**
 * Country + calling-code data for phone inputs.
 *
 * The country list comes from libphonenumber's own metadata rather than countries.json:
 * a calling code is only useful next to the numbering rules for that code, and the two
 * have to come from the same source to agree. Names are localised with Intl.DisplayNames
 * in the active UI locale, so the list reads "Almanya" in Turkish and "Germany" in English.
 */
export function usePhoneCountries() {
  const { locale } = useI18n()

  const phoneCountries = computed(() => {
    let displayNames = null
    try {
      displayNames = new Intl.DisplayNames([locale.value], { type: 'region' })
    } catch {
      // An engine without DisplayNames still gets a usable list, keyed by ISO code.
    }

    return getCountries()
      .map(iso2 => ({
        iso2,
        dialCode: `+${getCountryCallingCode(iso2)}`,
        name: displayNames?.of(iso2) || iso2
      }))
      .sort((a, b) => a.name.localeCompare(b.name, locale.value))
  })

  /**
   * The region the browser reports (e.g. "de-DE" → "DE"), if libphonenumber knows it.
   * A guess, not a fact — the supplier can always change it.
   */
  const guessDefaultCountry = () => {
    const supported = new Set(getCountries())
    const tags = navigator.languages?.length ? navigator.languages : [navigator.language]
    const region = tags
      .map(tag => tag?.split('-')[1]?.toUpperCase())
      .find(code => code && supported.has(code))
    return region || DEFAULT_PHONE_COUNTRY
  }

  /**
   * Parse a number in the context of the chosen country.
   *
   * `national` is the standard grouping without the dial code or trunk prefix
   * ("538 290 39 26", not "0538 290 39 26"), because the dial code is already shown
   * beside the field.
   *
   * @returns {{ valid: boolean, e164: string | null, national: string | null,
   *             country: string | null, dialCode: string | null }}
   */
  const parsePhone = (value, iso2) => {
    const parsed = parsePhoneNumberFromString(value || '', iso2)
    if (!parsed?.isValid()) {
      return { valid: false, e164: null, national: null, country: null, dialCode: null }
    }
    const dialCode = `+${parsed.countryCallingCode}`
    return {
      valid: true,
      e164: parsed.number,
      national: parsed.formatInternational().slice(dialCode.length).trim(),
      country: parsed.country || null,
      dialCode
    }
  }

  /** Standard digit grouping for a partially typed number. */
  const formatAsYouType = (value, iso2) => new AsYouType(iso2).input(value)

  return { phoneCountries, guessDefaultCountry, parsePhone, formatAsYouType }
}
